import { useForm } from "react-hook-form";
import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import { DefaultFormMultiAutocomplete } from "../../../../../../common/components/default_form_multi_autocomplete";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo } from "react";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import { ChecklistServicoDropDown } from "../../../../domain/models/checklist_servico_dropdown";
import {
  initialObraAreaEmpreendimentoChecklist,
  ObraAreaEmpreendimentoChecklistForm,
  ObraAreaEmpreendimentoChecklistSchema,
} from "../../../schemas/Obras_cadastroarea/create_obra_area_empreendimento";
import { EmpreendimentoBlocoCompletoTable } from "../../../../../incorporacao/domain/models/empreendimentoBlocoTable";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import FormContainer from "../../../../../../common/components/form_container";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";

/**
 * ObraAreaEmpreendimentoChecklistModal
 *
 * Cria, de uma só vez, a unidade no bloco/pavimento do empreendimento e a área de checklist dela.
 * A descrição da área segue o padrão "BLOCO - PAVIMENTO - UNIDADE".
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  blocos: EmpreendimentoBlocoCompletoTable[];
  checklistServicos: ChecklistServicoDropDown[];
  onClose: () => void;
  onSubmit: (form: ObraAreaEmpreendimentoChecklistForm) => void;
};

type BlocoOption = {
  idIncorporacaoBloco: number;
  descricao: string;
};

type AndarOption = {
  idIncorporacaoAndar: number;
  nroAndar: number | null;
  descricao: string;
};

export const ObraAreaEmpreendimentoChecklistModal = ({
  open,
  saving,
  loading,
  blocos,
  checklistServicos,
  onClose,
  onSubmit,
}: Props) => {
  const hookForm = useForm<ObraAreaEmpreendimentoChecklistForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: initialObraAreaEmpreendimentoChecklist,
    resolver: zodResolver(ObraAreaEmpreendimentoChecklistSchema),
  });

  useEffect(() => {
    if (open) {
      hookForm.reset(initialObraAreaEmpreendimentoChecklist);
    }
  }, [
    open,
    hookForm,
  ]);

  const idIncorporacaoBloco = hookForm.watch('idIncorporacaoBloco');
  const idIncorporacaoAndar = hookForm.watch('idIncorporacaoAndar');
  const unidade = hookForm.watch('unidade');

  const blocoOptions = useMemo((): BlocoOption[] => {
    return blocos
      .filter(x => x.idIncorporacaoBloco)
      .map(x => ({
        idIncorporacaoBloco: x.idIncorporacaoBloco!,
        descricao: x.nomeBloco ?? 'Bloco',
      }));
  }, [blocos]);

  const selectedBloco = useMemo(() => {
    return blocos.find(x => x.idIncorporacaoBloco === idIncorporacaoBloco) ?? null;
  }, [blocos, idIncorporacaoBloco]);

  const andarOptions = useMemo((): AndarOption[] => {
    if (!selectedBloco) return [];

    return selectedBloco.listIncorporacaoAndar
      .filter(x => !x.delete)
      .sort((a, b) => (a.nroAndar ?? 0) - (b.nroAndar ?? 0))
      .map(x => ({
        idIncorporacaoAndar: x.idIncorporacaoAndar,
        nroAndar: x.nroAndar ?? null,
        descricao: `${x.nroAndar}° Pavimento`,
      }));
  }, [selectedBloco]);

  // Blocos de casas não têm pavimentos: a unidade fica direto no bloco.
  const blocoTemPavimentos = andarOptions.length > 0;

  const descricaoArea = useMemo(() => {
    const andar = andarOptions.find(x => x.idIncorporacaoAndar === idIncorporacaoAndar);
    return [selectedBloco?.nomeBloco, andar?.nroAndar?.toString(), unidade?.trim()]
      .filter(x => x)
      .join(' - ');
  }, [andarOptions, idIncorporacaoAndar, selectedBloco, unidade]);

  const handleChangeBloco = useCallback(() => {
    hookForm.setValue('idIncorporacaoAndar', null);
  }, [hookForm]);

  const handleSubmit = useCallback((form: ObraAreaEmpreendimentoChecklistForm) => {
    if (blocoTemPavimentos && !form.idIncorporacaoAndar) {
      hookForm.setError('idIncorporacaoAndar', { message: 'Este campo deve ser preenchido!' });
      return;
    }

    onSubmit({
      ...form,
      idIncorporacaoAndar: blocoTemPavimentos ? form.idIncorporacaoAndar : null,
    });
  }, [
    blocoTemPavimentos,
    hookForm,
    onSubmit,
  ]);

  const statusOptions = useMemo(() => {
    return [
      {
        id: 0,
        label: 'Em aberto',
        style: {
          backgroundColor: '#f8cb00',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 1,
        label: 'Finalizada',
        style: {
          backgroundColor: '#f3565d',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
    ];
  }, [
  ]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title='Checklist de área'
      width="50rem"
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(handleSubmit)}>
      <FormContainer title="Informações" arrangement="fullRow">
        <>
          <FormRadioGroupInput
            name='status'
            label='Status'
            options={statusOptions}
            reactHookForm={hookForm}
          />

          <DefaultFormAutocomplete
            name='idIncorporacaoBloco'
            label='Local'
            required={true}
            options={blocoOptions}
            getOptionKey={x => x.idIncorporacaoBloco}
            getOptionLabel={x => x.descricao}
            onChangeValue={handleChangeBloco}
            reactHookForm={hookForm}
          />

          <DefaultFormAutocomplete
            name='idIncorporacaoAndar'
            label='Pavimento'
            required={blocoTemPavimentos}
            readOnly={!blocoTemPavimentos}
            placeholder={selectedBloco && !blocoTemPavimentos ? 'Bloco sem pavimentos' : undefined}
            options={andarOptions}
            getOptionKey={x => x.idIncorporacaoAndar}
            getOptionLabel={x => x.descricao}
            reactHookForm={hookForm}
          />

          <DefaultFormInput
            name='unidade'
            label='Unidade'
            required={true}
            reactHookForm={hookForm}
          />

          {descricaoArea &&
            <div className="text-sm text-gray-600 dark:text-gray-300">
              Área: <span className="font-semibold">{descricaoArea}</span>
            </div>
          }

          <DefaultFormMultiAutocomplete
            name='checklists'
            label='Checklists'
            getOptionKey={x => x.idChecklistServico}
            getOptionLabel={x => x.descricao ?? ''}
            options={checklistServicos}
            chipMaxLength={75}
            reactHookForm={hookForm}
          />
        </>
      </FormContainer>
    </BaseFormControlledModal >
  );
}
